import { z } from 'zod'
import type { Validator } from './validated-form'

/**
 * Parse FormData into a nested object, handling both bracket notation
 * (`foo[0][bar]`) and dot notation (`foo[0].bar`, `foo.bar`).
 *
 * Both matter: the object-mode combobox emits `name[id]`/`name[name]`/`name[value]`,
 * while every array field built by the `Typed*Form` wrappers emits
 * `${arrayFieldName}[${arrayIndex}].${field}`. Splitting on `[` alone left the
 * dotted tail glued to the index (`0].name`), so array rows parsed as an object
 * and their schemas failed with "expected array, received object".
 */
function parseFormData(formData: FormData): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of formData.entries()) {
    const parts = key.match(/[^.[\]]+/g) ?? [key]
    let current: any = result
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      const nextPart = parts[i + 1]
      const isNextIndex = /^\d+$/.test(nextPart)
      if (current[part] == null) {
        current[part] = isNextIndex ? [] : {}
      }
      current = current[part]
    }
    const last = parts[parts.length - 1]
    current[last] = value
  }

  return result
}

export function withZod<T extends z.ZodType>(schema: T): Validator<z.infer<T>> {
  return {
    validate: async (data) => {
      const parsed = data instanceof FormData ? parseFormData(data) : data
      const result = schema.safeParse(parsed)
      if (result.success) return { data: result.data }
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        // Key errors the way the inputs are named (`patients[0].name`), not the
        // way zod paths read (`patients.0.name`), or `useField` never finds them
        // and an invalid row fails silently. Ancestors are keyed too, so an
        // object-valued field such as a combobox can show its own leaf's error.
        let path = ''
        for (const segment of issue.path) {
          path =
            typeof segment === 'number' ? `${path}[${segment}]` : path ? `${path}.${String(segment)}` : String(segment)
          if (!fieldErrors[path]) fieldErrors[path] = issue.message
        }
      }
      return { error: { fieldErrors } }
    },
  }
}
